import { type StoreData, StoreDataSchema } from "@bitwarden/web-vault/app/models/store/store.data";

export interface IModelCloneable<T> {
  clone(): T;
}

export interface IModelConvertible<T> {
  toStoreModel(): T;
}

export interface IView {
  readonly id: string;
}

export interface IModelView<S extends number = number, T = StoreData>
  extends IView,
    IModelConvertible<StoreDataSchema<S, T>> {}

export abstract class VaultView {
  /** Public properties **/
  dateModified: Date = new Date();

  /** Readonly and private Field **/
  protected dateCreated: Date = new Date();

  /** System Fields **/
  protected vaultId: string | null = null;
  protected organizationId: string | null = null;
  protected version: number = -1;

  isOrganizationOwned(id?: string): boolean {
    if (id) {
      return this.organizationId === id;
    }
    return !!this.organizationId;
  }
}

/**
 * Base class for vault model views that provides common functionality for handling store data and vault metadata.
 * This class should be extended by specific model view implementations for different data types.
 * It implements the IModelView interface and provides a structure for converting between store models and view models.
 * The toDataModel method must be implemented by subclasses to define how to convert the view back to its corresponding data model.
 */
export abstract class VaultModelView<S extends number, T>
  extends VaultView
  implements IModelView<S, T>
{
  readonly id: string;

  constructor(model: StoreDataSchema<S, T>) {
    super();
    this.id = model.id;
    this.version = model.v;
    this.vaultId = model.vid;
    this.organizationId = model.oid ?? null;
    const parsedDc = new Date(model.dc);
    this.dateCreated = isNaN(parsedDc.getTime()) ? new Date() : parsedDc;
    const parsedDm = new Date(model.dm);
    this.dateModified = isNaN(parsedDm.getTime()) ? new Date() : parsedDm;
  }

  protected abstract toDataModel(): T;

  private toVaultModel(): StoreDataSchema<S> {
    return {
      id: this.id,
      dc: this.dateCreated.toISOString(),
      dm: this.dateModified.toISOString(),
      v: this.version as S,
      vid: this.vaultId,
    };
  }

  toStoreModel() {
    const a = {
      ...this.toDataModel(),
      ...this.toVaultModel(),
    };
    return a;
  }
}

// TODO: Remove generic type and use StoreData directly
/**
 * @deprecated This ModelView class is deprecated and should not be used for new implementations. Use specific view classes that extend VaultModelView instead.
 */
export abstract class ModelView<Type extends StoreData = StoreData>
  implements IModelView, IModelCloneable<ModelView<Type>>
{
  /** Readonly and private Field **/
  readonly id: string;
  readonly dateCreated: Date;

  /** System Fields **/
  protected vaultId: string | null;
  protected organizationId: string | null;
  protected version: number;

  /** Public properties **/
  dateModified: Date;

  protected constructor(model?: Type) {
    if (model) {
      this.id = model.id;
      this.vaultId = model.vid;
      this.organizationId = model.oid ?? null;
      const parsedDc = new Date(model.dc);
      this.dateCreated = isNaN(parsedDc.getTime()) ? new Date() : parsedDc;
      const parsedDm = new Date(model.dm);
      this.dateModified = isNaN(parsedDm.getTime()) ? new Date() : parsedDm;
      this.version = model.v;
    } else {
      this.id = crypto.randomUUID();
      this.vaultId = null; // View object outside of fromStoreModel do not have vaultId properties.
      this.organizationId = null; // View object outside of fromStoreModel do not have organizationId properties.
      this.dateCreated = new Date();
      this.dateModified = new Date();
      this.version = -1; // Unkown Version?
    }
  }

  isOrganizationOwned(id?: string) {
    if (id) {
      return this.organizationId === id;
    }
    return !!this.organizationId;
  }

  getOrganizationId(): string | null {
    return this.organizationId;
  }

  getVaultId(): string | null {
    return this.vaultId;
  }

  isSyncInVault() {
    return !!this.vaultId;
  }

  abstract clone(): ModelView<Type>;
  /** oid is a read-only reference recomputed from the cipher; VaultWriter.syncToVault strips it from every model before it's queued for persistence, regardless of what this returns. **/
  abstract toStoreModel(): Type;
}
